import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import {
    PredictionBarChart,
    ComparisonAndConfidence,
    PredictionMarketPotentialBestOutbounds,
    DownloadLoads,
    RpmVsDistance,
    LockedWrapper,
    LockedTooltip,
} from "@/components/dataPages";
import { useIsDownloadsEnabled } from "@/services/featureFlags";
import { cn } from "@/utils/css";
import { setInputSearch, clearInputs } from "@/state/searchInputSlice";
import { setCurrentSearch, clearCurrentSearch } from "@/state/currentSearchSlice";
import { lockedSearchInputMarketPotential } from "@/utils/lockedData";

export const LockedMarketPotential = () => {
    const [activeTooltip, setActiveTooltip] = useState(null);
    const downloadsEnabled = useIsDownloadsEnabled();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            setInputSearch({
                pickup: lockedSearchInputMarketPotential.pickup,
                pickupQuery: lockedSearchInputMarketPotential.pickupQuery,
                pickupRadius: lockedSearchInputMarketPotential.pickupRadius,
                trailerType: lockedSearchInputMarketPotential.trailerType,
                pickupDate: lockedSearchInputMarketPotential.pickupDate,
            }),
        );
        dispatch(
            setCurrentSearch({
                pickup: lockedSearchInputMarketPotential.pickup,
                trailerType: lockedSearchInputMarketPotential.trailerType,
                pickupDate: lockedSearchInputMarketPotential.pickupDate,
                pickupRadius: lockedSearchInputMarketPotential.pickupRadius,
            }),
        );

        return () => {
            dispatch(clearInputs());
            dispatch(clearCurrentSearch());
        };
    }, [dispatch]);

    const handleHover = (component) => {
        setActiveTooltip(component);
    };

    const handleLeave = () => {
        setActiveTooltip(null);
    };

    return (
        <div className="relative">
            <div className="flex flex-col lg:grid lg:grid-cols-[330px_calc(100%_-_350px)] lg:gap-[20px] items-start w-full h-full pt-1">
                <div
                    className={cn(
                        "relative z-10 flex flex-col lg:mr-5 w-full bg-cm-p-surface pt-5 pb-6 px-6 drop-shadow-[0_4px_4px_rgba(0,0,0,0.05)] rounded-lg min-h-[688px] justify-between",
                        {
                            "min-h-[747px]": downloadsEnabled,
                        },
                    )}
                >
                    <LockedWrapper
                        isActive={activeTooltip === 'leftSide'}
                        onHover={() => handleHover('leftSide')}
                        onLeave={handleLeave}
                    >
                        <RpmVsDistance isLocked />
                    <div className="w-full h-[1px] bg-cm-p-outline-variation my-[26px]" />
                        <ComparisonAndConfidence />
                    <div className="w-full h-[1px] bg-cm-p-outline-variation my-[26px]" />
                    <div className="w-full">
                            <PredictionBarChart
                                title="Outb. load volume"
                                tooltipText="Shows the daily outbound load volume from this market, expressed as a percentage."
                                isLocked
                            />
                    </div>
                    </LockedWrapper>
                    <DownloadLoads filePath={null} className="mt-4" />

                </div>
                <div className="relative z-10 flex w-full bg-cm-p-surface pt-4 pb-6 px-8 drop-shadow-[0_4px_4px_rgba(0,0,0,0.05)] rounded-lg lg:min-h-full">
                    <PredictionMarketPotentialBestOutbounds 
                        isLocked={true}
                        activeTooltip={activeTooltip}
                        onHover={handleHover}
                        onLeave={handleLeave}
                    />
                </div>
            </div>
            <LockedTooltip
                content={<>See the <span className="font-bold">likelihood of securing a load</span> in any region, using insights from historical patterns, current shifts in the market, and typical load volume.</>}
                tooltipId="leftSide"
                activeTooltip={activeTooltip}
                width="415px"
                positionConfig={{
                    default: { mediumScreen: { top: '269px', left: '211px' }, desktop: { top: '269px', left: '211px' } },
                    active: { mediumScreen: { top: '269px', left: '317px' }, desktop: { top: '269px', left: '317px' } },
                    outboundCards: { mediumScreen: { top: '243px', left: '131px' }, desktop: { top: '243px', left: '131px' } },
                    prediction: { mediumScreen: { top: '369px', left: '187px' }, desktop: { top: '269px', left: '187px' } },
                    map: { mediumScreen: { top: '400px', left: '-11px' }, desktop: { top: '269px', left: '-41px' } },
                }}
            />
        </div>
    );
};